import type { LucideIcon } from 'lucide-react'

import {
    CalendarHeart,
    Compass,
    EyeOff,
    Gem,
    Hourglass,
    Infinity as InfinityIcon,
    KeyRound,
    Sparkles
} from 'lucide-react'

export const NAV_LINKS = [
    { label: "Collection", href: '#collection' },
    { label: "The House", href: '#house' },
    { label: "Savoir-faire", href: '#savoir-faire' },
    { label: "Codes", href: '#codes' },
    { label: "Le Cercle", href: '#cercle' }
]

export const MARQUEE_ITEMS = [
    "Haute maroquinerie",
    "Fine jewelry",
    "Evening gloves",
    "Made by hand in Paris",
    "Numbered editions",
    "Care for life"
]

export type StoryChapter = {
    year: string
    place: string
    title: string
    text: string
    focus: { scale: number, x: number, y: number }
}

export const STORY_CHAPTERS: StoryChapter[] = [
    {
        year: "1908",
        place: "Faubourg Saint-Honoré",
        title: "A glove-maker opens her door",
        text: "Céleste Aveline rented two small rooms in Paris and cut evening gloves for the couture houses on her street. From the first pair she kept one rule: everything would be made from beginning to end by a single pair of hands.",
        focus: { scale: 1.04, x: 0, y: 0 }
    },
    {
        year: "1926",
        place: "The first evening bag",
        title: "Something to carry the gloves in",
        text: "When a client asked for a bag to hold her gloves at the opera, Céleste cut a soft frame in black calfskin and hung it on two brass handles. Le Céleste is still made to her pattern, nearly a century later.",
        focus: { scale: 1.34, x: 9, y: 3 }
    },
    {
        year: "1954",
        place: "The atelier upstairs",
        title: "Gold and stone, by the north light",
        text: "Her son Henri opened a jewelry workshop on the floor above, setting stones by the even light of the courtyard windows. Every Aveline jewel is still drawn by hand before it is cast.",
        focus: { scale: 1.38, x: -10, y: 4 }
    },
    {
        year: "Today",
        place: "Four generations",
        title: "Still family, still one pair of hands",
        text: "The house remains in the family and in the same Paris street. Our artisans train for three years before they sign a piece, and every object still leaves the bench of the person who began it.",
        focus: { scale: 1.14, x: 0, y: -5 }
    }
]

export const CRAFT_STATS = [
    {
        value: "22",
        unit: "hours",
        text: "to cut, stitch and burnish a single Céleste, all at one bench."
    },
    {
        value: "1",
        unit: "pair of hands",
        text: "from the first cut to the final polish. Their initials are stamped inside."
    },
    {
        value: "3",
        unit: "years of training",
        text: "before an artisan is trusted to sign a piece with the house name."
    },
    {
        value: "4",
        unit: "generations",
        text: "of the Aveline family, in the same Paris street since 1908."
    }
]

export const CRAFT_STEPS = [
    {
        step: "01",
        title: "Selection",
        text: "Hides are chosen one at a time at two family tanneries in France and Italy."
    },
    {
        step: "02",
        title: "Cutting",
        text: "Every panel is cut by hand with a round knife, following the grain of the leather."
    },
    {
        step: "03",
        title: "Stitching",
        text: "Saddle-stitched with waxed linen thread, two needles at a time, so no seam can unravel."
    },
    {
        step: "04",
        title: "Finishing",
        text: "Edges are painted and burnished in up to seven coats, then polished by hand."
    }
]

export type HouseCode = {
    title: string
    text: string
    icon: LucideIcon
}

export const HOUSE_CODES: HouseCode[] = [
    {
        title: "Provenance",
        text: "Our leathers come from two family tanneries, and our gold is recycled and certified. We can tell you where every piece began.",
        icon: Compass
    },
    {
        title: "Patience",
        text: "A piece leaves the atelier when it is finished, never when a season demands it. Some take a day. Some take a month.",
        icon: Hourglass
    },
    {
        title: "Permanence",
        text: "Made to be repaired, never replaced. Every Aveline piece is cared for in our ateliers for as long as it is worn.",
        icon: InfinityIcon
    },
    {
        title: "Discretion",
        text: "No logos on the outside, ever. An Aveline piece is recognized by those who know, and by no one else.",
        icon: EyeOff
    }
]

export const MEMBER_VOICES = [
    {
        quote: "My grandmother's bag went back to the atelier after sixty years. It came home with a new silk lining and the very same stitches.",
        name: "Hélène D.",
        detail: "Lyon, member since 2009",
        initials: "HD"
    },
    {
        quote: "I was invited to watch my ring being set. Two hours, one artisan, and not a single wasted movement.",
        name: "James W.",
        detail: "London, member since 2016",
        initials: "JW"
    },
    {
        quote: "Nothing is announced and nothing is loud. A letter arrives, and the new collection is simply there for you first.",
        name: "Aiko T.",
        detail: "Tokyo, member since 2021",
        initials: "AT"
    }
]

export type CercleBenefit = {
    title: string
    text: string
    icon: LucideIcon
}

export const CERCLE_BENEFITS: CercleBenefit[] = [
    {
        title: "First access",
        text: "Every collection opens to members before it is shown publicly.",
        icon: KeyRound
    },
    {
        title: "A personal client advisor",
        text: "One person who knows your pieces, reachable by phone or message.",
        icon: Gem
    },
    {
        title: "Atelier evenings",
        text: "Private viewings and seasonal dinners in our Paris salon.",
        icon: CalendarHeart
    },
    {
        title: "Engraving and lifetime care",
        text: "Complimentary on every piece, for as long as it is worn.",
        icon: Sparkles
    }
]

export const DEFAULT_INTERESTS = ["Handbags", "Fine Jewelry", "Gloves"]
